import styled from '@emotion/styled';
import type { ErrorInfo, ReactNode } from 'react';
import { Component } from 'react';
import { borderRadius, colors, spacing, typography } from '@/lib/design-tokens';

/* ────────────── Types ────────────── */

interface FallbackRenderProps {
  error: unknown;
  resetErrorBoundary: () => void;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  /** 固定のフォールバック要素 */
  fallback?: ReactNode;
  /** エラー情報を受け取るフォールバックレンダラー */
  fallbackRender?: (props: FallbackRenderProps) => ReactNode;
  /** エラー発生時のコールバック */
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  error: unknown;
}

/* ────────────── ErrorBoundary (Class Component) ────────────── */

/**
 * React Error Boundary
 *
 * 子コンポーネントのレンダリングエラーをキャッチし、
 * フォールバック UI を表示する。
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    this.props.onError?.(error, info);
  }

  resetErrorBoundary = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    const { error } = this.state;
    const { children, fallback, fallbackRender } = this.props;

    if (error !== null) {
      if (fallbackRender) {
        return fallbackRender({ error, resetErrorBoundary: this.resetErrorBoundary });
      }
      if (fallback) {
        return fallback;
      }
      return null;
    }

    return children;
  }
}

/* ────────────── Styled Components ────────────── */

const FallbackContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${spacing[500]};
  text-align: center;
  min-height: 200px;
  gap: ${spacing[300]};
`;

const FallbackTitle = styled.h2`
  font-family: ${typography.fontFamily.default};
  font-size: ${typography.fontSize.h600};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.red[700]};
  margin: 0;
`;

const FallbackMessage = styled.p`
  font-family: ${typography.fontFamily.default};
  font-size: ${typography.fontSize.body};
  color: ${colors.neutral[600]};
  margin: 0;
  max-width: 480px;
  line-height: ${typography.lineHeight.normal};
`;

const RetryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 16px;
  height: 40px;
  font-family: ${typography.fontFamily.default};
  font-size: ${typography.fontSize.body};
  font-weight: ${typography.fontWeight.medium};
  color: ${colors.neutral[0]};
  background-color: ${colors.brand.blue[600]};
  border: none;
  border-radius: ${borderRadius.default};
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${colors.brand.blue[700]};
  }

  &:focus-visible {
    outline: 2px solid ${colors.brand.blue[300]};
    outline-offset: 2px;
  }
`;

const GlobalContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding: ${spacing[500]};
  text-align: center;
  gap: ${spacing[300]};
`;

const GlobalTitle = styled.h1`
  font-family: ${typography.fontFamily.default};
  font-size: ${typography.fontSize.h700};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.red[700]};
  margin: 0;
`;

const BackLink = styled.a`
  display: inline-block;
  margin-top: ${spacing[200]};
  padding: 10px 20px;
  background-color: ${colors.brand.blue[600]};
  color: ${colors.neutral[0]};
  border-radius: ${borderRadius.default};
  font-family: ${typography.fontFamily.default};
  font-size: ${typography.fontSize.body};
  font-weight: ${typography.fontWeight.medium};
  text-decoration: none;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${colors.brand.blue[700]};
  }

  &:focus-visible {
    outline: 2px solid ${colors.brand.blue[300]};
    outline-offset: 2px;
  }
`;

/* ────────────── Fallback Components ────────────── */

interface ErrorFallbackProps {
  error: unknown;
  resetErrorBoundary: () => void;
}

/**
 * フィーチャー単位のエラーフォールバック
 *
 * 個別機能のエラーをキャッチし、再読み込みボタンを提供する。
 */
function FeatureErrorFallback({ error, resetErrorBoundary }: ErrorFallbackProps) {
  const message = error instanceof Error ? error.message : '不明なエラーが発生しました';

  return (
    <FallbackContainer role="alert">
      <FallbackTitle>エラーが発生しました</FallbackTitle>
      <FallbackMessage>{message}</FallbackMessage>
      <RetryButton type="button" onClick={resetErrorBoundary}>
        再読み込み
      </RetryButton>
    </FallbackContainer>
  );
}

/**
 * グローバルフォールバック
 *
 * アプリ全体のキャッチされなかったエラーを表示する。
 * ダッシュボードへの導線を提供する。
 */
function GlobalErrorFallback({ error }: ErrorFallbackProps) {
  const message = error instanceof Error ? error.message : '不明なエラーが発生しました';

  return (
    <GlobalContainer role="alert">
      <GlobalTitle>予期しないエラーが発生しました</GlobalTitle>
      <FallbackMessage>{message}</FallbackMessage>
      <BackLink href="/dashboard">ダッシュボードへ戻る</BackLink>
    </GlobalContainer>
  );
}

export type { ErrorBoundaryProps, ErrorFallbackProps, FallbackRenderProps };
export { ErrorBoundary, FeatureErrorFallback, GlobalErrorFallback };
